import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { 
  Award, GraduationCap, Users, ChevronRight, BookOpen, 
  Star, Trophy, Target, Waves, BarChart3, ArrowRight
} from 'lucide-react';

// Certification levels data extracted from document
export const CERTIFICATION_DATA = {
  CMAS: {
    name: "CMAS",
    fullName: "Confédération Mondiale des Activités Subaquatiques",
    description: "International federation that coordinates freediving activities worldwide. Certifications are usually delivered via affiliated national federations like FFESSM.",
    descriptionFr: "Fédération internationale qui coordonne les activités de plongée libre dans le monde entier. Les certifications sont généralement délivrées via des fédérations nationales affiliées comme la FFESSM.",
    color: "blue",
    levels: [
      {
        code: "CMAS_DECOUVERTE",
        name: "Apnée Découverte",
        nameEn: "Discovery Freediving",
        category: "beginner",
        description: "Introduction to freediving. First contact with breath-hold diving in a safe environment.",
        descriptionFr: "Introduction à l'apnée. Premier contact avec la plongée en apnée dans un environnement sécurisé."
      },
      {
        code: "CMAS_N1",
        name: "Apnéiste Niveau I",
        nameEn: "Freediver Level I",
        category: "beginner",
        description: "First level of freediver certification. Includes basic freediving theory, breath-hold techniques, and initial depth progression.",
        descriptionFr: "Premier niveau de certification apnéiste. Comprend la théorie de base, les techniques de retenue du souffle et la progression initiale en profondeur."
      },
      {
        code: "CMAS_N2",
        name: "Apnéiste Niveau II",
        nameEn: "Freediver Level II",
        category: "intermediate",
        description: "Intermediate freediver certification with increased depth capability and technique proficiency.",
        descriptionFr: "Certification apnéiste intermédiaire avec une capacité de profondeur accrue et une maîtrise technique améliorée."
      },
      {
        code: "CMAS_N3",
        name: "Apnéiste Niveau III",
        nameEn: "Freediver Level III",
        category: "advanced",
        description: "Advanced freediver certification with significant depth and endurance capabilities.",
        descriptionFr: "Certification apnéiste avancée avec des capacités significatives de profondeur et d'endurance."
      },
      {
        code: "CMAS_MN1",
        name: "Moniteur Apnée Niveau 1",
        nameEn: "Freediving Instructor Level 1",
        category: "instructor",
        description: "Professional certification for teaching basic freediving courses.",
        descriptionFr: "Certification professionnelle pour enseigner les cours d'apnée de base."
      },
      {
        code: "CMAS_MN2",
        name: "Moniteur Apnée Niveau 2+",
        nameEn: "Freediving Instructor Level 2+",
        category: "instructor",
        description: "Higher level professional certification for teaching advanced freediving.",
        descriptionFr: "Certification professionnelle de niveau supérieur pour enseigner l'apnée avancée."
      }
    ]
  },
  AIDA: {
    name: "AIDA",
    fullName: "International Association for the Development of Apnea",
    description: "The world's premier freediving organization with a clear standardized star system for progression. Also organizes international competitions.",
    descriptionFr: "La première organisation mondiale d'apnée avec un système d'étoiles standardisé pour la progression. Organise également des compétitions internationales.",
    color: "teal",
    levels: [
      {
        code: "AIDA_1",
        name: "AIDA 1 ★",
        nameEn: "AIDA 1 Star Freediver",
        category: "beginner",
        description: "Beginner level freediving certification. Introduction to safe freediving practices.",
        descriptionFr: "Certification apnée niveau débutant. Introduction aux pratiques de plongée libre sécuritaires."
      },
      {
        code: "AIDA_2",
        name: "AIDA 2 ★★",
        nameEn: "AIDA 2 Star Freediver",
        category: "intermediate",
        description: "Intermediate level with pool and open water components. Depth progression up to 16-20m.",
        descriptionFr: "Niveau intermédiaire avec composantes piscine et eau libre. Progression jusqu'à 16-20m."
      },
      {
        code: "AIDA_3",
        name: "AIDA 3 ★★★",
        nameEn: "AIDA 3 Star Freediver",
        category: "advanced",
        description: "Advanced level freediving. Depth up to 24-30m with advanced rescue techniques.",
        descriptionFr: "Apnée niveau avancé. Profondeur jusqu'à 24-30m avec techniques de sauvetage avancées."
      },
      {
        code: "AIDA_4",
        name: "AIDA 4 ★★★★",
        nameEn: "AIDA 4 Star Freediver",
        category: "expert",
        description: "Highly advanced level. Depth beyond 32m with competition-level techniques.",
        descriptionFr: "Niveau très avancé. Profondeur au-delà de 32m avec techniques de niveau compétition."
      },
      {
        code: "AIDA_INSTRUCTOR",
        name: "AIDA Instructor",
        nameEn: "AIDA Instructor",
        category: "instructor",
        description: "Certification to teach AIDA freediving courses up to AIDA 2.",
        descriptionFr: "Certification pour enseigner les cours AIDA jusqu'au niveau AIDA 2."
      },
      {
        code: "AIDA_MASTER_INSTRUCTOR",
        name: "AIDA Master Instructor",
        nameEn: "AIDA Master Instructor",
        category: "instructor",
        description: "Higher level instructor certification to teach all AIDA courses.",
        descriptionFr: "Certification instructeur de niveau supérieur pour enseigner tous les cours AIDA."
      },
      {
        code: "AIDA_IT",
        name: "AIDA Instructor Trainer",
        nameEn: "AIDA Instructor Trainer",
        category: "instructor",
        description: "Highest level certification, qualified to train new instructors.",
        descriptionFr: "Certification de plus haut niveau, qualifié pour former de nouveaux instructeurs."
      }
    ]
  },
  Molchanovs: {
    name: "Molchanovs",
    fullName: "Molchanovs Freediving Education",
    description: "Modern education system founded by Alexey Molchanov. Features 'Lap' courses (pool only) and 'Wave' courses (including depth/open water).",
    descriptionFr: "Système éducatif moderne fondé par Alexey Molchanov. Propose des cours 'Lap' (piscine uniquement) et 'Wave' (incluant profondeur/eau libre).",
    color: "purple",
    levels: [
      {
        code: "MOLCH_LAP1",
        name: "Lap 1",
        nameEn: "Lap 1 - Pool Beginner",
        category: "beginner",
        description: "Beginner pool-only freediving course. Focus on breath-hold fundamentals.",
        descriptionFr: "Cours d'apnée débutant en piscine uniquement. Focus sur les fondamentaux de la retenue du souffle."
      },
      {
        code: "MOLCH_WAVE1",
        name: "Wave 1",
        nameEn: "Wave 1 - Beginner",
        category: "beginner",
        description: "Beginner course including pool and open water/depth sessions.",
        descriptionFr: "Cours débutant incluant piscine et sessions en eau libre/profondeur."
      },
      {
        code: "MOLCH_LAP2",
        name: "Lap 2",
        nameEn: "Lap 2 - Pool Intermediate",
        category: "intermediate",
        description: "Intermediate pool-only course with advanced techniques.",
        descriptionFr: "Cours intermédiaire en piscine uniquement avec techniques avancées."
      },
      {
        code: "MOLCH_WAVE2",
        name: "Wave 2",
        nameEn: "Wave 2 - Intermediate",
        category: "intermediate",
        description: "Intermediate course with depth progression and equalization techniques.",
        descriptionFr: "Cours intermédiaire avec progression en profondeur et techniques d'égalisation."
      },
      {
        code: "MOLCH_LAP3",
        name: "Lap 3",
        nameEn: "Lap 3 - Pool Advanced",
        category: "advanced",
        description: "Advanced pool training for competitive freedivers.",
        descriptionFr: "Entraînement avancé en piscine pour apnéistes compétiteurs."
      },
      {
        code: "MOLCH_WAVE3",
        name: "Wave 3",
        nameEn: "Wave 3 - Advanced",
        category: "advanced",
        description: "Advanced depth freediving with competitive techniques.",
        descriptionFr: "Apnée profonde avancée avec techniques compétitives."
      },
      {
        code: "MOLCH_LAP4",
        name: "Lap 4",
        nameEn: "Lap 4 - Pool Competitive",
        category: "expert",
        description: "Competitive level pool freediving.",
        descriptionFr: "Apnée piscine niveau compétition."
      },
      {
        code: "MOLCH_WAVE4",
        name: "Wave 4",
        nameEn: "Wave 4 - Competitive",
        category: "expert",
        description: "Very advanced competitive freediving course.",
        descriptionFr: "Cours d'apnée très avancé niveau compétition."
      },
      {
        code: "MOLCH_W1_INST",
        name: "Wave 1 Instructor",
        nameEn: "Wave 1 Instructor",
        category: "instructor",
        description: "Certification to teach Wave 1 courses.",
        descriptionFr: "Certification pour enseigner les cours Wave 1."
      },
      {
        code: "MOLCH_W2_INST",
        name: "Wave 2 Instructor",
        nameEn: "Wave 2 Instructor",
        category: "instructor",
        description: "Certification to teach Wave 1-2 courses.",
        descriptionFr: "Certification pour enseigner les cours Wave 1-2."
      },
      {
        code: "MOLCH_W3_INST",
        name: "Wave 3 Instructor",
        nameEn: "Wave 3 Instructor",
        category: "instructor",
        description: "Certification to teach Wave 1-3 courses.",
        descriptionFr: "Certification pour enseigner les cours Wave 1-3."
      },
      {
        code: "MOLCH_MASTER",
        name: "Master Instructor",
        nameEn: "Master Instructor",
        category: "instructor",
        description: "Higher level instructor certification.",
        descriptionFr: "Certification instructeur de niveau supérieur."
      }
    ]
  },
  ApneaAcademy: {
    name: "Apnea Academy",
    fullName: "Apnea Academy",
    description: "Founded by legendary freediver Umberto Pelizzari. Oriented towards technical performance with a focus on proper technique.",
    descriptionFr: "Fondée par le légendaire apnéiste Umberto Pelizzari. Orientée vers la performance technique avec un accent sur la technique appropriée.",
    color: "orange",
    levels: [
      {
        code: "AA_DISCOVERY",
        name: "Apnea Discovery",
        nameEn: "Apnea Discovery",
        category: "beginner",
        description: "Initiation course into freediving fundamentals.",
        descriptionFr: "Cours d'initiation aux fondamentaux de l'apnée."
      },
      {
        code: "AA_INDOOR",
        name: "Indoor Freediver",
        nameEn: "Indoor Freediver",
        category: "beginner",
        description: "Pool-focused freediving certification.",
        descriptionFr: "Certification apnée axée sur la piscine."
      },
      {
        code: "AA_LEVEL1",
        name: "First Level Freediver",
        nameEn: "First Level Freediver",
        category: "beginner",
        description: "First level of freediver certification with depth introduction.",
        descriptionFr: "Premier niveau de certification apnéiste avec introduction à la profondeur."
      },
      {
        code: "AA_LEVEL2",
        name: "Second Level Freediver",
        nameEn: "Second Level Freediver",
        category: "intermediate",
        description: "Intermediate freediver with advanced depth techniques.",
        descriptionFr: "Apnéiste intermédiaire avec techniques de profondeur avancées."
      },
      {
        code: "AA_LEVEL3",
        name: "Third Level Freediver",
        nameEn: "Third Level Freediver",
        category: "advanced",
        description: "Advanced freediver certification for experienced divers.",
        descriptionFr: "Certification apnéiste avancée pour plongeurs expérimentés."
      },
      {
        code: "AA_STAGE",
        name: "Apnea Academy Stage",
        nameEn: "Apnea Academy Stage",
        category: "advanced",
        description: "Intensive training courses for performance improvement.",
        descriptionFr: "Cours de formation intensive pour améliorer la performance."
      },
      {
        code: "AA_INSTRUCTOR",
        name: "Apnea Academy Instructor",
        nameEn: "Apnea Academy Instructor",
        category: "instructor",
        description: "Professional instructor certification.",
        descriptionFr: "Certification professionnelle d'instructeur."
      }
    ]
  },
  PADI: {
    name: "PADI",
    fullName: "Professional Association of Diving Instructors",
    description: "World's largest scuba diving organization, also offers a dedicated freediving curriculum.",
    descriptionFr: "La plus grande organisation de plongée sous-marine au monde, propose également un programme dédié à l'apnée.",
    color: "yellow",
    levels: [
      {
        code: "PADI_FREEDIVER",
        name: "PADI Freediver",
        nameEn: "PADI Freediver",
        category: "beginner",
        description: "Entry-level freediver certification covering basics of breath-hold diving.",
        descriptionFr: "Certification apnéiste de niveau d'entrée couvrant les bases de la plongée en apnée."
      },
      {
        code: "PADI_ADVANCED",
        name: "PADI Advanced Freediver",
        nameEn: "PADI Advanced Freediver",
        category: "intermediate",
        description: "Intermediate certification with deeper depths and longer breath-holds.",
        descriptionFr: "Certification intermédiaire avec des profondeurs plus importantes et des apnées plus longues."
      },
      {
        code: "PADI_MASTER",
        name: "PADI Master Freediver",
        nameEn: "PADI Master Freediver",
        category: "advanced",
        description: "Advanced certification for experienced freedivers.",
        descriptionFr: "Certification avancée pour apnéistes expérimentés."
      },
      {
        code: "PADI_INST",
        name: "PADI Freediver Instructor",
        nameEn: "PADI Freediver Instructor",
        category: "instructor",
        description: "Professional certification to teach PADI Freediver courses.",
        descriptionFr: "Certification professionnelle pour enseigner les cours PADI Freediver."
      },
      {
        code: "PADI_MASTER_INST",
        name: "PADI Master Freediver Instructor",
        nameEn: "PADI Master Freediver Instructor",
        category: "instructor",
        description: "Higher level instructor certification.",
        descriptionFr: "Certification instructeur de niveau supérieur."
      }
    ]
  },
  SSI: {
    name: "SSI",
    fullName: "Scuba Schools International",
    description: "International diving organization with a progressive level structure for freediving.",
    descriptionFr: "Organisation de plongée internationale avec une structure de niveaux progressive pour l'apnée.",
    color: "green",
    levels: [
      {
        code: "SSI_BASIC",
        name: "SSI Basic Freediver",
        nameEn: "SSI Basic Freediver",
        category: "beginner",
        description: "Basic introduction to freediving fundamentals.",
        descriptionFr: "Introduction de base aux fondamentaux de l'apnée."
      },
      {
        code: "SSI_POOL",
        name: "SSI Pool Freediver",
        nameEn: "SSI Pool Freediver",
        category: "beginner",
        description: "Pool-focused freediving certification.",
        descriptionFr: "Certification apnée axée sur la piscine."
      },
      {
        code: "SSI_FREEDIVER",
        name: "SSI Freediver",
        nameEn: "SSI Freediver",
        category: "intermediate",
        description: "Standard freediver certification with pool and open water.",
        descriptionFr: "Certification apnéiste standard avec piscine et eau libre."
      },
      {
        code: "SSI_ADVANCED",
        name: "SSI Advanced Freediver",
        nameEn: "SSI Advanced Freediver",
        category: "advanced",
        description: "Advanced certification for deeper dives.",
        descriptionFr: "Certification avancée pour des plongées plus profondes."
      },
      {
        code: "SSI_PERFORMANCE",
        name: "SSI Performance Freediver",
        nameEn: "SSI Performance Freediver",
        category: "expert",
        description: "Highest performance level for competitive freedivers.",
        descriptionFr: "Niveau de performance le plus élevé pour apnéistes compétiteurs."
      },
      {
        code: "SSI_BASIC_INST",
        name: "SSI Basic Freediving Instructor",
        nameEn: "SSI Basic Freediving Instructor",
        category: "instructor",
        description: "Certification to teach basic SSI freediving courses.",
        descriptionFr: "Certification pour enseigner les cours de base SSI."
      },
      {
        code: "SSI_ASSIST_INST",
        name: "SSI Freediving Assistant Instructor",
        nameEn: "SSI Freediving Assistant Instructor",
        category: "instructor",
        description: "Assistant instructor certification.",
        descriptionFr: "Certification assistant instructeur."
      },
      {
        code: "SSI_INST",
        name: "SSI Freediving Instructor",
        nameEn: "SSI Freediving Instructor",
        category: "instructor",
        description: "Full instructor certification.",
        descriptionFr: "Certification instructeur complète."
      },
      {
        code: "SSI_ADV_INST",
        name: "SSI Advanced Freediving Instructor",
        nameEn: "SSI Advanced Freediving Instructor",
        category: "instructor",
        description: "Advanced instructor certification.",
        descriptionFr: "Certification instructeur avancée."
      },
      {
        code: "SSI_PERF_INST",
        name: "SSI Performance Freediving Instructor",
        nameEn: "SSI Performance Freediving Instructor",
        category: "instructor",
        description: "Highest level performance instructor.",
        descriptionFr: "Instructeur de performance de plus haut niveau."
      }
    ]
  },
  FFESSM: {
    name: "FFESSM",
    fullName: "Fédération Française d'Études et de Sports Sous-Marins",
    description: "French national federation affiliated with CMAS. Offers localized certifications aligned with CMAS standards.",
    descriptionFr: "Fédération nationale française affiliée à la CMAS. Propose des certifications localisées alignées sur les normes CMAS.",
    color: "indigo",
    levels: [
      {
        code: "FFESSM_CREVETTES",
        name: "ATP Crevettes",
        nameEn: "ATP Shrimp (Youth)",
        category: "beginner",
        description: "Certification for young freedivers (children/youth program).",
        descriptionFr: "Certification pour jeunes apnéistes (programme enfants/jeunes)."
      },
      {
        code: "FFESSM_PISCINE",
        name: "Apnéiste Piscine",
        nameEn: "Pool Freediver",
        category: "beginner",
        description: "Basic pool-based freediver certification.",
        descriptionFr: "Certification apnéiste de base en piscine."
      },
      {
        code: "FFESSM_CONFIRME",
        name: "Apnéiste Confirmé",
        nameEn: "Confirmed Freediver",
        category: "intermediate",
        description: "Confirmed freediver for pool and open water.",
        descriptionFr: "Apnéiste confirmé pour piscine et eau libre."
      },
      {
        code: "FFESSM_EXPERT",
        name: "Apnéiste Expert",
        nameEn: "Expert Freediver",
        category: "advanced",
        description: "Expert freediver certification for open water.",
        descriptionFr: "Certification apnéiste expert pour eau libre."
      },
      {
        code: "FFESSM_IE1",
        name: "Initiateur-Entraîneur IE1",
        nameEn: "Coach-Instructor IE1",
        category: "instructor",
        description: "First level coaching and training certification.",
        descriptionFr: "Certification entraîneur-initiateur premier niveau."
      },
      {
        code: "FFESSM_IE2",
        name: "Initiateur-Entraîneur IE2",
        nameEn: "Coach-Instructor IE2",
        category: "instructor",
        description: "Second level coaching and training certification.",
        descriptionFr: "Certification entraîneur-initiateur deuxième niveau."
      }
    ]
  },
  Other: {
    name: "Other",
    fullName: "Other Organizations",
    description: "Various other freediving organizations including FII, PFI, RAID, NAUI, and more. Often have equivalences with main systems.",
    descriptionFr: "Diverses autres organisations d'apnée incluant FII, PFI, RAID, NAUI et plus. Ont souvent des équivalences avec les systèmes principaux.",
    color: "gray",
    levels: [
      {
        code: "FII_1",
        name: "FII Freediver Level 1",
        nameEn: "FII Freediver Level 1",
        category: "beginner",
        description: "Freediving International Inc. beginner level.",
        descriptionFr: "Freediving International Inc. niveau débutant."
      },
      {
        code: "FII_2",
        name: "FII Freediver Level 2",
        nameEn: "FII Freediver Level 2",
        category: "intermediate",
        description: "FII intermediate level.",
        descriptionFr: "FII niveau intermédiaire."
      },
      {
        code: "FII_3",
        name: "FII Freediver Level 3",
        nameEn: "FII Freediver Level 3",
        category: "advanced",
        description: "FII advanced level.",
        descriptionFr: "FII niveau avancé."
      },
      {
        code: "PFI_FREEDIVER",
        name: "PFI Freediver",
        nameEn: "PFI Freediver",
        category: "intermediate",
        description: "Performance Freediving International certification.",
        descriptionFr: "Certification Performance Freediving International."
      },
      {
        code: "PFI_INSTRUCTOR",
        name: "PFI Instructor",
        nameEn: "PFI Instructor",
        category: "instructor",
        description: "PFI instructor certification.",
        descriptionFr: "Certification instructeur PFI."
      },
      {
        code: "OTHER",
        name: "Other Certification",
        nameEn: "Other Certification",
        category: "other",
        description: "Other certification not listed above.",
        descriptionFr: "Autre certification non listée ci-dessus."
      }
    ]
  }
};

// Certification Equivalence Data - Approximate level mappings across agencies
const EQUIVALENCE_DATA = {
  beginner: {
    label: { en: "Beginner", fr: "Débutant" },
    color: "green",
    levels: [
      { agency: "CMAS", level: "Apnée Découverte / Niveau I" },
      { agency: "AIDA", level: "AIDA 1 ★" },
      { agency: "Molchanovs", level: "Lap 1 / Wave 1" },
      { agency: "Apnea Academy", level: "Discovery / Indoor" },
      { agency: "PADI", level: "PADI Freediver" },
      { agency: "SSI", level: "Basic / Pool Freediver" },
      { agency: "FFESSM", level: "Apnéiste Piscine" }
    ]
  },
  intermediate: {
    label: { en: "Intermediate", fr: "Intermédiaire" },
    color: "blue",
    levels: [
      { agency: "CMAS", level: "Apnéiste Niveau II" },
      { agency: "AIDA", level: "AIDA 2 ★★" },
      { agency: "Molchanovs", level: "Lap 2 / Wave 2" },
      { agency: "Apnea Academy", level: "First Level Freediver" },
      { agency: "PADI", level: "PADI Advanced Freediver" },
      { agency: "SSI", level: "SSI Freediver" },
      { agency: "FFESSM", level: "Apnéiste Confirmé" }
    ]
  },
  advanced: {
    label: { en: "Advanced", fr: "Avancé" },
    color: "orange",
    levels: [
      { agency: "CMAS", level: "Apnéiste Niveau III" },
      { agency: "AIDA", level: "AIDA 3 ★★★" },
      { agency: "Molchanovs", level: "Lap 3 / Wave 3" },
      { agency: "Apnea Academy", level: "Second Level Freediver" },
      { agency: "PADI", level: "PADI Master Freediver" },
      { agency: "SSI", level: "SSI Advanced Freediver" },
      { agency: "FFESSM", level: "Apnéiste Expert" }
    ]
  },
  expert: {
    label: { en: "Expert / Competitive", fr: "Expert / Compétition" },
    color: "red",
    levels: [
      { agency: "CMAS", level: "—" },
      { agency: "AIDA", level: "AIDA 4 ★★★★" },
      { agency: "Molchanovs", level: "Lap 4 / Wave 4" },
      { agency: "Apnea Academy", level: "Third Level / Stage" },
      { agency: "PADI", level: "—" },
      { agency: "SSI", level: "SSI Performance Freediver" },
      { agency: "FFESSM", level: "—" }
    ]
  },
  instructor: {
    label: { en: "Instructor", fr: "Instructeur" },
    color: "purple",
    levels: [
      { agency: "CMAS", level: "Moniteur N1 / N2+" },
      { agency: "AIDA", level: "Instructor / Master / IT" },
      { agency: "Molchanovs", level: "Wave 1-3 Instructor / Master" },
      { agency: "Apnea Academy", level: "AA Instructor" },
      { agency: "PADI", level: "Freediver Instructor / Master" },
      { agency: "SSI", level: "Basic to Performance Instructor" },
      { agency: "FFESSM", level: "IE1 / IE2" }
    ]
  }
};

const getCategoryIcon = (category) => {
  switch (category) {
    case 'beginner': return <Star className="text-green-400" size={16} />;
    case 'intermediate': return <Target className="text-blue-400" size={16} />;
    case 'advanced': return <Trophy className="text-orange-400" size={16} />;
    case 'expert': return <Award className="text-red-400" size={16} />;
    case 'instructor': return <GraduationCap className="text-purple-400" size={16} />;
    default: return <BookOpen className="text-gray-400" size={16} />;
  }
};

const getCategoryBadgeClass = (category) => {
  switch (category) {
    case 'beginner': return 'bg-green-600/50 text-green-200 border-green-500';
    case 'intermediate': return 'bg-blue-600/50 text-blue-200 border-blue-500';
    case 'advanced': return 'bg-orange-600/50 text-orange-200 border-orange-500';
    case 'expert': return 'bg-red-600/50 text-red-200 border-red-500';
    case 'instructor': return 'bg-purple-600/50 text-purple-200 border-purple-500';
    default: return 'bg-gray-600/50 text-gray-200 border-gray-500';
  }
};

const getCategoryLabel = (category, t) => {
  switch (category) {
    case 'beginner': return t('beginner');
    case 'intermediate': return t('intermediate');
    case 'advanced': return t('advanced');
    case 'expert': return t('expert');
    case 'instructor': return t('instructor');
    default: return t('other');
  }
};

const getAgencyColor = (color) => {
  const colors = {
    blue: 'from-blue-800/40 to-blue-900/40 border-blue-600/30',
    teal: 'from-teal-800/40 to-teal-900/40 border-teal-600/30',
    purple: 'from-purple-800/40 to-purple-900/40 border-purple-600/30',
    orange: 'from-orange-800/40 to-orange-900/40 border-orange-600/30',
    yellow: 'from-yellow-800/40 to-yellow-900/40 border-yellow-600/30',
    green: 'from-green-800/40 to-green-900/40 border-green-600/30',
    indigo: 'from-indigo-800/40 to-indigo-900/40 border-indigo-600/30',
    gray: 'from-gray-800/40 to-gray-900/40 border-gray-600/30'
  };
  return colors[color] || colors.blue;
};

const FreedivingLevelsTab = ({ t, language, selectedAgency = null }) => {
  const [expandedAgency, setExpandedAgency] = useState(selectedAgency);

  const agencies = Object.keys(CERTIFICATION_DATA);

  // If equivalence chart is selected, show the equivalence view
  if (selectedAgency === 'equivalence') {
    return (
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold text-blue-200">
              {language === 'fr' ? "Tableau d'Équivalence" : "Equivalence Chart"}
            </h2>
            <p className="text-blue-300 mt-1">
              {language === 'fr' 
                ? "Comparaison approximative des niveaux de certification entre organismes" 
                : "Approximate certification level comparison across agencies"}
            </p>
          </div>
          <Badge className="bg-purple-600/50 text-purple-200 px-4 py-2">
            <BarChart3 className="mr-2" size={16} />
            7 {language === 'fr' ? 'organismes' : 'agencies'}
          </Badge>
        </div>

        {/* Important Note */}
        <div className="bg-amber-900/30 border border-amber-600/50 rounded-lg p-4">
          <p className="text-amber-200 text-sm">
            <strong>{language === 'fr' ? 'Note importante:' : 'Important note:'}</strong>{' '}
            {language === 'fr' 
              ? "Ces équivalences sont approximatives. Chaque organisme a ses propres exigences spécifiques. Consultez toujours l'organisme concerné pour les reconnaissances officielles."
              : "These equivalences are approximate. Each agency has its own specific requirements. Always consult the relevant agency for official recognitions."}
          </p>
        </div>

        {/* Equivalence Table */}
        <Card className="bg-gradient-to-br from-slate-800/60 to-slate-900/60 border border-slate-600/30">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-800/80">
                    <th className="px-4 py-3 text-left text-slate-300 font-semibold border-b border-slate-700">
                      {language === 'fr' ? 'Niveau' : 'Level'}
                    </th>
                    {['CMAS', 'AIDA', 'Molchanovs', 'Apnea Academy', 'PADI', 'SSI', 'FFESSM'].map(agency => (
                      <th key={agency} className="px-4 py-3 text-center text-slate-300 font-semibold border-b border-slate-700 min-w-[140px]">
                        {agency}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {Object.entries(EQUIVALENCE_DATA).map(([category, data], idx) => {
                    const bgColors = {
                      green: 'bg-green-900/20',
                      blue: 'bg-blue-900/20',
                      orange: 'bg-orange-900/20',
                      red: 'bg-red-900/20',
                      purple: 'bg-purple-900/20'
                    };
                    const textColors = {
                      green: 'text-green-300',
                      blue: 'text-blue-300',
                      orange: 'text-orange-300',
                      red: 'text-red-300',
                      purple: 'text-purple-300'
                    };
                    return (
                      <tr key={category} className={`${bgColors[data.color]} hover:bg-slate-700/30 transition-colors`}>
                        <td className={`px-4 py-4 font-semibold ${textColors[data.color]} border-b border-slate-700/50`}>
                          <div className="flex items-center gap-2">
                            {getCategoryIcon(category)}
                            {language === 'fr' ? data.label.fr : data.label.en}
                          </div>
                        </td>
                        {data.levels.map((levelInfo, levelIdx) => (
                          <td key={levelIdx} className="px-4 py-4 text-center text-slate-200 text-sm border-b border-slate-700/50">
                            {levelInfo.level === '—' ? (
                              <span className="text-slate-500">—</span>
                            ) : (
                              levelInfo.level
                            )}
                          </td>
                        ))}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Visual Progression Guide */}
        <Card className="bg-gradient-to-br from-blue-900/40 to-teal-900/40 border border-blue-600/30">
          <CardHeader>
            <CardTitle className="text-blue-200 flex items-center gap-2">
              <ArrowRight size={20} />
              {language === 'fr' ? 'Progression Typique' : 'Typical Progression'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap items-center justify-center gap-4">
              {Object.entries(EQUIVALENCE_DATA).map(([category, data], idx, arr) => {
                const bgColors = {
                  green: 'bg-green-600/50 border-green-500',
                  blue: 'bg-blue-600/50 border-blue-500',
                  orange: 'bg-orange-600/50 border-orange-500',
                  red: 'bg-red-600/50 border-red-500',
                  purple: 'bg-purple-600/50 border-purple-500'
                };
                return (
                  <React.Fragment key={category}>
                    <div className={`px-4 py-2 rounded-lg border ${bgColors[data.color]} text-white font-medium`}>
                      {language === 'fr' ? data.label.fr : data.label.en}
                    </div>
                    {idx < arr.length - 1 && (
                      <ArrowRight className="text-slate-400" size={20} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
            <p className="text-center text-slate-400 text-sm mt-4">
              {language === 'fr' 
                ? "La progression vers les niveaux instructeur nécessite généralement une expérience significative et des formations supplémentaires."
                : "Progression to instructor levels typically requires significant experience and additional training."}
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // If a specific agency is selected via prop, show only that agency
  const agenciesToShow = selectedAgency ? [selectedAgency] : agencies;

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-3xl font-bold text-blue-200">{t('freedivingLevels')}</h2>
          <p className="text-blue-300 mt-1">{t('freedivingLevelsDescription')}</p>
        </div>
        <Badge className="bg-blue-600/50 text-blue-200 px-4 py-2">
          <Users className="mr-2" size={16} />
          {agencies.length} {t('agencies')}
        </Badge>
      </div>

      {/* Agency Cards */}
      <div className="space-y-4">
        {agenciesToShow.map((agencyKey) => {
          const agency = CERTIFICATION_DATA[agencyKey];
          const isExpanded = expandedAgency === agencyKey || selectedAgency === agencyKey;
          
          // Group levels by category
          const levelsByCategory = {
            beginner: agency.levels.filter(l => l.category === 'beginner'),
            intermediate: agency.levels.filter(l => l.category === 'intermediate'),
            advanced: agency.levels.filter(l => l.category === 'advanced'),
            expert: agency.levels.filter(l => l.category === 'expert'),
            instructor: agency.levels.filter(l => l.category === 'instructor'),
            other: agency.levels.filter(l => l.category === 'other')
          };

          return (
            <Card 
              key={agencyKey}
              className={`bg-gradient-to-br ${getAgencyColor(agency.color)} border backdrop-blur-sm transition-all duration-300`}
            >
              <CardHeader 
                className={`cursor-pointer ${!selectedAgency ? 'hover:bg-white/5' : ''}`}
                onClick={() => !selectedAgency && setExpandedAgency(isExpanded ? null : agencyKey)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="w-12 h-12 rounded-full bg-white/10 flex items-center justify-center">
                      <Waves className="text-white" size={24} />
                    </div>
                    <div>
                      <CardTitle className="text-xl text-white flex items-center gap-2">
                        {agency.name}
                        <Badge variant="outline" className="text-xs border-white/30 text-white/70">
                          {agency.levels.length} {t('levels')}
                        </Badge>
                      </CardTitle>
                      <p className="text-sm text-white/70">{agency.fullName}</p>
                    </div>
                  </div>
                  {!selectedAgency && (
                    <ChevronRight 
                      className={`text-white/50 transition-transform ${isExpanded ? 'rotate-90' : ''}`} 
                      size={24} 
                    />
                  )}
                </div>
                <p className="text-white/80 mt-3 text-sm">
                  {language === 'fr' ? agency.descriptionFr : agency.description}
                </p>
              </CardHeader>

              {isExpanded && (
                <CardContent className="pt-0">
                  <div className="border-t border-white/10 pt-4 space-y-6">
                    {/* Beginner Levels */}
                    {levelsByCategory.beginner.length > 0 && (
                      <div>
                        <h4 className="text-green-300 font-semibold mb-3 flex items-center gap-2">
                          <Star size={16} />
                          {t('beginnerLevels')}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {levelsByCategory.beginner.map((level) => (
                            <div key={level.code} className="p-3 bg-green-900/20 rounded-lg border border-green-600/30">
                              <div className="flex items-center gap-2 mb-2">
                                {getCategoryIcon(level.category)}
                                <span className="font-medium text-white">{level.name}</span>
                              </div>
                              <p className="text-sm text-white/70">
                                {language === 'fr' ? level.descriptionFr : level.description}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Intermediate Levels */}
                    {levelsByCategory.intermediate.length > 0 && (
                      <div>
                        <h4 className="text-blue-300 font-semibold mb-3 flex items-center gap-2">
                          <Target size={16} />
                          {t('intermediateLevels')}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {levelsByCategory.intermediate.map((level) => (
                            <div key={level.code} className="p-3 bg-blue-900/20 rounded-lg border border-blue-600/30">
                              <div className="flex items-center gap-2 mb-2">
                                {getCategoryIcon(level.category)}
                                <span className="font-medium text-white">{level.name}</span>
                              </div>
                              <p className="text-sm text-white/70">
                                {language === 'fr' ? level.descriptionFr : level.description}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Advanced Levels */}
                    {levelsByCategory.advanced.length > 0 && (
                      <div>
                        <h4 className="text-orange-300 font-semibold mb-3 flex items-center gap-2">
                          <Trophy size={16} />
                          {t('advancedLevels')}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {levelsByCategory.advanced.map((level) => (
                            <div key={level.code} className="p-3 bg-orange-900/20 rounded-lg border border-orange-600/30">
                              <div className="flex items-center gap-2 mb-2">
                                {getCategoryIcon(level.category)}
                                <span className="font-medium text-white">{level.name}</span>
                              </div>
                              <p className="text-sm text-white/70">
                                {language === 'fr' ? level.descriptionFr : level.description}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Expert Levels */}
                    {levelsByCategory.expert.length > 0 && (
                      <div>
                        <h4 className="text-red-300 font-semibold mb-3 flex items-center gap-2">
                          <Award size={16} />
                          {t('expertLevels')}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {levelsByCategory.expert.map((level) => (
                            <div key={level.code} className="p-3 bg-red-900/20 rounded-lg border border-red-600/30">
                              <div className="flex items-center gap-2 mb-2">
                                {getCategoryIcon(level.category)}
                                <span className="font-medium text-white">{level.name}</span>
                              </div>
                              <p className="text-sm text-white/70">
                                {language === 'fr' ? level.descriptionFr : level.description}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Instructor Levels */}
                    {levelsByCategory.instructor.length > 0 && (
                      <div>
                        <h4 className="text-purple-300 font-semibold mb-3 flex items-center gap-2">
                          <GraduationCap size={16} />
                          {t('instructorLevels')}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {levelsByCategory.instructor.map((level) => (
                            <div key={level.code} className="p-3 bg-purple-900/20 rounded-lg border border-purple-600/30">
                              <div className="flex items-center gap-2 mb-2">
                                {getCategoryIcon(level.category)}
                                <span className="font-medium text-white">{level.name}</span>
                              </div>
                              <p className="text-sm text-white/70">
                                {language === 'fr' ? level.descriptionFr : level.description}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Other Levels */}
                    {levelsByCategory.other.length > 0 && (
                      <div>
                        <h4 className="text-gray-300 font-semibold mb-3 flex items-center gap-2">
                          <BookOpen size={16} />
                          {t('otherLevels')}
                        </h4>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          {levelsByCategory.other.map((level) => (
                            <div key={level.code} className="p-3 bg-gray-900/20 rounded-lg border border-gray-600/30">
                              <div className="flex items-center gap-2 mb-2">
                                {getCategoryIcon(level.category)}
                                <span className="font-medium text-white">{level.name}</span>
                              </div>
                              <p className="text-sm text-white/70">
                                {language === 'fr' ? level.descriptionFr : level.description}
                              </p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </CardContent>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
};

export default FreedivingLevelsTab;
